import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import userService from '../services/userService';
import { useNotification } from '../context/NotificationContext';

const UserFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const { notify } = useNotification();
  const isEdit = !!id;
  const [formData, setFormData] = useState({ email: '', name: '', phone: '', is_staff: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      userService.getUser(token, id).then(user => {
        setFormData({ email: user.email, name: user.name, phone: user.phone || '', is_staff: user.is_staff });
      }).catch(err => notify.error(t('common.failedToLoad')));
    }
    // Runs once per id: refetching when `notify`/`t` change would overwrite
    // whatever the user has already typed into the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.name || !formData.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (isEdit) {
        await userService.updateUser(token, id, formData);
        notify.success(t('common.updatedSuccessfully'));
      } else {
        await userService.createUser(token, formData);
        notify.success(t('users.createdConfirmationSent'));
      }
      setTimeout(() => navigate('/users'), 2000);
    } catch (err) {
      notify.error(err.response?.data?.detail || t('common.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px' }}>
      <h2 style={{ color: '#3D3D3D', marginBottom: '20px' }}>
        {isEdit ? 'Edit User' : 'Create New User'}
      </h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email {!isEdit && <span style={{ color: '#F22B55' }}>*</span>}</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            disabled={isEdit}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.email ? '2px solid #F22B55' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              opacity: isEdit ? 0.6 : 1
            }}
          />
          {errors.email && <p style={{ color: '#F22B55', fontSize: '12px' }}>{errors.email}</p>}
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name <span style={{ color: '#F22B55' }}>*</span></label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.name ? '2px solid #F22B55' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
          {errors.name && <p style={{ color: '#F22B55', fontSize: '12px' }}>{errors.name}</p>}
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '30px' }}>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={formData.is_staff}
              onChange={(e) => setFormData({ ...formData, is_staff: e.target.checked })}
              style={{ marginRight: '10px' }}
            />
            <span>Make this user an admin</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: '#A0BD46',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/users')}
            style={{
              backgroundColor: '#ccc',
              color: '#333',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserFormPage;
